import { useState, useCallback, useEffect } from 'react';
import { Droplets, Check, Info } from 'lucide-react';
import LabBench from '../../LabBench';
import { Dropper, LitmusPaper } from '@/components/lab';
import InfoBox from '../../InfoBox';

// Educational content for each step
const stepInfoContent: Record<number, {
  title: string;
  content: string;
  whatHappens: string;
  observe: string[];
  equation?: string;
}> = {
  1: {
    title: 'Preparing the Copper Solution',
    content: 'Copper(II) solutions contain the [Cu(H₂O)₆]²⁺ complex, which gives them their characteristic light blue color. The water molecules coordinate to the copper ion in an octahedral arrangement. This hydrated copper ion is what we will react with ammonia.',
    whatHappens: 'You are adding 3 mL of Cu²⁺ solution to a clean test tube. The light blue color confirms the presence of hydrated copper(II) ions.',
    observe: [
      'Solution appears light blue/cyan',
      'Color is from [Cu(H₂O)₆]²⁺ complex',
      'Solution is clear, not cloudy'
    ]
  },
  2: {
    title: 'Adding Ammonia (NH₄OH)',
    content: 'When ammonia solution is added dropwise to Cu²⁺, it first forms a light blue precipitate of copper(II) hydroxide, Cu(OH)₂. As more ammonia is added, the hydroxide dissolves and is replaced by ammonia molecules coordinating to the copper, forming the deep blue tetraamminecopper(II) complex [Cu(NH₃)₄]²⁺.',
    whatHappens: 'Initially, Cu(OH)₂ precipitates (cloudy light blue). With excess ammonia, this dissolves to form the intensely colored [Cu(NH₃)₄]²⁺ complex ion. This is called a ligand exchange reaction.',
    observe: [
      'First drops: cloudy light blue precipitate forms',
      'More drops: precipitate begins to dissolve',
      'Excess: deep blue solution appears'
    ],
    equation: 'Cu²⁺ + 4NH₃ → [Cu(NH₃)₄]²⁺ (deep blue colour)'
  },
  3: {
    title: 'Testing pH with Litmus',
    content: 'Red litmus paper turns blue in basic (alkaline) solutions. The ammonia solution provides hydroxide ions (OH-) that make the solution basic with pH > 7. Confirming basic pH is important because the copper-ammonia complex forms most completely in basic conditions.',
    whatHappens: 'When you dip red litmus paper into the solution, it will turn blue, confirming that the solution is basic. This is expected because we added excess ammonia.',
    observe: [
      'Red litmus paper turns blue',
      'Confirms pH > 7 (basic)',
      'Ammonia is responsible for the basic pH'
    ]
  },
  4: {
    title: 'Observing the Copper-Ammonia Complex',
    content: 'The deep blue colour of [Cu(NH₃)₄]²⁺ is caused by d-d electronic transitions. When light passes through the solution, the copper ion absorbs orange/red wavelengths and transmits blue light. This intense colour is diagnostic for copper and is used in quantitative analysis.',
    whatHappens: 'You observe the distinctive deep blue colour that confirms the formation of the tetraamminecopper(II) complex. This colour is much more intense than the original hydrated copper ion.',
    observe: [
      'Color is deep blue, not light blue',
      'Color is uniform throughout the solution',
      'No precipitate should remain if enough NH₃ was added'
    ]
  },
  5: {
    title: 'Recording the Positive Result',
    content: 'The deep blue colour of [Cu(NH₃)₄]²⁺ is a POSITIVE confirmatory test for copper(II) ions. This test is highly selective - no other common cation produces this distinctive colour with ammonia. In qualitative analysis, a positive result means the ion is present.',
    whatHappens: 'You record that the test is positive for Cu²⁺. The formation of the deep blue tetraamminecopper(II) complex is definitive evidence for the presence of copper(II) ions.',
    observe: [
      'Positive result: deep blue colour confirms Cu²⁺',
      'This test is specific to copper',
      'Record observations in your lab notebook'
    ],
    equation: 'Cu²⁺ + 4NH₃ → [Cu(NH₃)₄]²⁺ (confirms Cu²⁺)'
  }
};

interface Part2CopperTestProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

// Color progression for copper-ammonia complex
const copperColors = {
  initial: '#64B5F6',       // Light blue - hydrated copper(II)
  intermediate: '#90CAF9',  // Cloudy light blue - Cu(OH)₂ precipitate
  final: '#1565C0',         // Deep blue - [Cu(NH₃)₄]²⁺
};

export default function Part2CopperTest({ onStepComplete, currentStep = 1 }: Part2CopperTestProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);
  const [cuSolutionAdded, setCuSolutionAdded] = useState(false);
  const [nh4ohDrops, setNh4ohDrops] = useState(0);
  const [isDropping, setIsDropping] = useState(false);
  const [pHTested, setPHTested] = useState(false);
  const [litmusColor, setLitmusColor] = useState<'red' | 'blue'>('red');
  const [colorObserved, setColorObserved] = useState(false);
  const [resultRecorded, setResultRecorded] = useState(false);

  // Calculate current solution color based on NH4OH drops
  const getCurrentColor = () => {
    if (nh4ohDrops === 0) return copperColors.initial;
    if (nh4ohDrops < 5) return copperColors.intermediate;
    return copperColors.final;
  };

  const getFillLevel = () => {
    if (!cuSolutionAdded) return 0;
    return 50 + nh4ohDrops * 2;
  };

  const handleAddCuSolution = useCallback(() => {
    setCuSolutionAdded(true);
    if (onStepComplete) onStepComplete('copper-1');
  }, [onStepComplete]);

  const handleAddNH4OH = useCallback(() => {
    if (cuSolutionAdded && nh4ohDrops < 8) {
      setIsDropping(true);
      setTimeout(() => {
        setIsDropping(false);
        setNh4ohDrops(prev => {
          const newDrops = prev + 1;
          if (newDrops >= 5 && onStepComplete) {
            onStepComplete('copper-2');
          }
          return newDrops;
        });
      }, 500);
    }
  }, [cuSolutionAdded, nh4ohDrops, onStepComplete]);

  const handleTestpH = useCallback(() => {
    if (nh4ohDrops >= 5) {
      setLitmusColor('blue');
      setPHTested(true);
      if (onStepComplete) onStepComplete('copper-3');
    }
  }, [nh4ohDrops, onStepComplete]);

  const handleObserveColor = useCallback(() => {
    if (pHTested) {
      setColorObserved(true);
      if (onStepComplete) onStepComplete('copper-4');
    }
  }, [pHTested, onStepComplete]);

  const handleRecordResult = useCallback(() => {
    if (colorObserved) {
      setResultRecorded(true);
      if (onStepComplete) onStepComplete('copper-5');
    }
  }, [colorObserved, onStepComplete]);

  return (
    <LabBench
      experimentName="Copper Confirmation Test"
      completedCount={[cuSolutionAdded, nh4ohDrops >= 5, pHTested, colorObserved, resultRecorded].filter(Boolean).length}
      totalSteps={5}
      equipment={['Cu²⁺ solution', 'Concentrated NH₄OH', 'Litmus paper', 'Test tube']}
      labMode="guided"
      currentStep={currentStep}
    >
      <div className="copper-test">
        {/* Equipment Area */}
        <div className="equipment-row">
          {/* Test Tube with Cu²⁺ solution */}
          <div className="equipment-section">
            <svg viewBox="0 0 60 130" width={60} height={130}>
              <defs>
                <linearGradient id="tube-glass-cu" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
                <linearGradient id="cu-liquid" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={getCurrentColor()} stopOpacity="0.85" />
                  <stop offset="100%" stopColor={getCurrentColor()} stopOpacity="0.95" />
                </linearGradient>
              </defs>

              {/* Test tube rim */}
              <ellipse cx="30" cy="15" rx="15" ry="5" fill="rgba(200,220,240,0.5)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Test tube body */}
              <path d="M15 15 L15 100 Q15 115 30 115 Q45 115 45 100 L45 15" fill="url(#tube-glass-cu)" stroke="#8BA8C4" strokeWidth="1.5" />

              {/* Liquid fill */}
              {cuSolutionAdded && (
                <path
                  d={`M17 ${115 - getFillLevel()} L17 98 Q17 112 30 112 Q43 112 43 98 L43 ${115 - getFillLevel()} Z`}
                  fill="url(#cu-liquid)"
                >
                  {/* Color transition animation */}
                  <animate
                    attributeName="opacity"
                    values="0.85;0.95;0.85"
                    dur="2s"
                    repeatCount="indefinite"
                  />
                </path>
              )}

              {/* Cloudiness effect when intermediate */}
              {nh4ohDrops > 0 && nh4ohDrops < 5 && (
                <g opacity="0.4">
                  <ellipse cx="25" cy="80" rx="5" ry="3" fill="#B3E5FC" />
                  <ellipse cx="35" cy="70" rx="4" ry="2" fill="#B3E5FC" />
                  <ellipse cx="28" cy="90" rx="3" ry="2" fill="#B3E5FC" />
                </g>
              )}

              {/* Glass shine */}
              <line x1="19" y1="20" x2="19" y2="90" stroke="rgba(255,255,255,0.5)" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <span className="equipment-label">Cu²⁺ Solution</span>
          </div>

          {/* Dropper */}
          <div className="equipment-section">
            <Dropper
              label="Conc. NH₄OH"
              liquidColor="#F5F5F5"
              onClick={cuSolutionAdded && !resultRecorded ? handleAddNH4OH : undefined}
              active={cuSolutionAdded && nh4ohDrops < 8}
              dropping={isDropping}
              disabled={!cuSolutionAdded || nh4ohDrops >= 8}
            />
          </div>

          {/* Litmus Paper */}
          <div className="equipment-section">
            <LitmusPaper
              color={litmusColor}
              onClick={nh4ohDrops >= 5 && !pHTested ? handleTestpH : undefined}
              active={nh4ohDrops >= 5 && !pHTested}
              disabled={nh4ohDrops < 5 || pHTested}
            />
          </div>
        </div>

        {/* Status Messages */}
        <div className="status-section">
          {!cuSolutionAdded && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 1</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                >
                  ?
                </button>
              </div>
              <p>Add 3 mL of <strong>Cu²⁺ solution</strong> to a clean test tube.</p>
              <button className="action-btn" onClick={handleAddCuSolution}>
                <Droplets size={16} />
                Add Cu²⁺ Solution
              </button>
            </div>
          )}

          {cuSolutionAdded && nh4ohDrops < 5 && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 2</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
                >
                  ?
                </button>
              </div>
              <p>Add <strong>concentrated NH₄OH dropwise</strong> until solution is basic. ({nh4ohDrops}/5+ drops)</p>
              <p className="hint">Click the dropper to add concentrated NH₄OH drops.</p>
            </div>
          )}

          {nh4ohDrops >= 5 && !pHTested && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 3</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(3); }}
                >
                  ?
                </button>
              </div>
              <p>Test the pH with <strong>red litmus paper</strong>.</p>
              <p className="hint">Click the litmus paper to test.</p>
            </div>
          )}

          {pHTested && !colorObserved && (
            <div className="instruction-card">
              <div className="instruction-header">
                <span className="step-badge">Step 4</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(4); }}
                >
                  ?
                </button>
              </div>
              <p>Observe the <strong>deep blue colour</strong> of the copper-ammonia complex.</p>
              <button className="action-btn" onClick={handleObserveColor}>
                <Check size={16} />
                Observe Color
              </button>
            </div>
          )}

          {colorObserved && !resultRecorded && (
            <div className="instruction-card success">
              <div className="instruction-header">
                <span className="step-badge">Step 5</span>
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(5); }}
                >
                  ?
                </button>
              </div>
              <p>Record the <strong>positive result</strong> for copper confirmation.</p>
              <button className="action-btn success" onClick={handleRecordResult}>
                <Check size={16} />
                Record Result
              </button>
            </div>
          )}

          {resultRecorded && (
            <div className="result-card">
              <Check size={20} />
              <div>
                <p><strong>Cu²⁺ Confirmed!</strong></p>
                <p>Deep blue colour indicates presence of copper ions [Cu(NH₃)₄]²⁺</p>
              </div>
            </div>
          )}
        </div>

        {/* pH indicator */}
        {pHTested && (
          <div className="ph-indicator">
            <span>pH Test:</span>
            <span className={`ph-result ${litmusColor}`}>
              Litmus turned {litmusColor} - Solution is {litmusColor === 'blue' ? 'BASIC' : 'ACIDIC'}
            </span>
          </div>
        )}

        {/* Theory Box */}
        <div className="theory-box">
          <h4><Info size={14} /> The Copper-Ammonia Complex</h4>
          <p>
            When excess concentrated ammonia (NH₃) is added to Cu²⁺ solution, it forms the deep blue
            tetraaminecopper(II) complex [Cu(NH₃)₄]²⁺. Initially, Cu(OH)₂ precipitates
            (light blue cloudy), but with excess ammonia, this dissolves.
          </p>
          <div className="equation">
            Cu<sup>2+</sup> + 4NH<sub>3</sub> → [Cu(NH<sub>3</sub>)<sub>4</sub>]<sup>2+</sup>
          </div>
        </div>

        <style>{`
          .copper-test {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
            max-width: 600px;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            align-items: flex-end;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          .status-section {
            width: 100%;
            min-height: 100px;
          }

          .instruction-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border-left: 4px solid var(--primary);
          }

          .instruction-card.success {
            border-left-color: #00A86B;
          }

          .step-badge {
            display: inline-block;
            background: var(--primary);
            color: white;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
            margin-bottom: 8px;
          }

          .instruction-card p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-primary);
          }

          .instruction-card .hint {
            font-size: 12px;
            color: var(--text-muted);
            font-style: italic;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
            margin-top: 8px;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .result-card {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 16px 20px;
            background: rgba(0, 168, 107, 0.1);
            border-radius: 12px;
            color: #00A86B;
          }

          .result-card p {
            margin: 0;
            color: var(--text-primary);
          }

          .result-card p:first-child {
            color: #00A86B;
            font-weight: 600;
          }

          .ph-indicator {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 13px;
          }

          .ph-result {
            font-weight: 600;
          }

          .ph-result.blue {
            color: #2563EB;
          }

          .ph-result.red {
            color: #DC2626;
          }

          .theory-box {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
          }

          .theory-box h4 {
            display: flex;
            align-items: center;
            gap: 8px;
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .theory-box p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-primary);
            line-height: 1.6;
          }

          .theory-box .equation {
            text-align: center;
            font-size: 14px;
            font-family: 'Times New Roman', serif;
            color: var(--text-secondary);
            background: var(--bg-dark);
            padding: 10px;
            border-radius: 6px;
          }

          .instruction-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 8px;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp2"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
    </LabBench>
  );
}
